// IMPORTANT: When adding new themes you must also update
//              - EnvironmentTheme enum in api/app/models/system/environment-config.entity.ts
//              - Translations in admin
//              - Branding controls in admin/src/pages/Developers/BrandingControls
export const environmentTheme = {
  Black: "Black",
  RedSlate: "RedSlate",
  NavyBlue: "NavyBlue",
  NavyAzure: "NavyAzure",
  Gold: "Gold",
  Orange: "Orange",
  Yellow: "Yellow",
  Green: "Green",
  Blue: "Blue",
  Purple: "Purple",
  Pink: "Pink",
} as const;

export type ThemeShades = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  1000: string;
};

export type Theme = {
  primary: ThemeShades;
  secondary: ThemeShades;
  neutral: ThemeShades;
  background: ThemeShades;
  accent: ThemeShades;
};

export const themes: Record<keyof typeof environmentTheme, Theme> = {
  [environmentTheme.Black]: {
    primary: {
      50: "#F5F5F5",
      100: "#D6D6D7",
      200: "#ACACAF",
      300: "#838386",
      400: "#59595E",
      500: "#303036",
      600: "#26262B",
      700: "#1D1D20",
      800: "#131316",
      900: "#0A0A0B",
      1000: "#020203",
    },
    secondary: {
      50: "#F4F4F4",
      100: "#D1D1D1",
      200: "#A3A3A3",
      300: "#757575",
      400: "#474747",
      500: "#191919",
      600: "#141414",
      700: "#0F0F0F",
      800: "#0A0A0A",
      900: "#050505",
      1000: "#010101",
    },
    accent: {
      50: "#F5F5F5",
      100: "#D6D6D7",
      200: "#ACACAF",
      300: "#838386",
      400: "#59595E",
      500: "#303036",
      600: "#26262B",
      700: "#1D1D20",
      800: "#131316",
      900: "#0A0A0B",
      1000: "#020203",
    },
    neutral: {
      50: "#F2F2F2",
      100: "#CCCCCC",
      200: "#999999",
      300: "#666666",
      400: "#333333",
      500: "#000000",
      600: "#000000",
      700: "#000000",
      800: "#000000",
      900: "#000000",
      1000: "#000000",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFC",
      300: "#FAFAFA",
      400: "#F8F8F9",
      500: "#f6f6f7",
      600: "#C5C5C6",
      700: "#949494",
      800: "#626263",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.RedSlate]: {
    primary: {
      50: "#FEF2F2",
      100: "#FBCCCC",
      200: "#F79999",
      300: "#F46766",
      400: "#F03433",
      500: "#ec0100",
      600: "#BD0100",
      700: "#8E0100",
      800: "#5E0000",
      900: "#2F0000",
      1000: "#0C0000",
    },
    secondary: {
      50: "#F4F5F5",
      100: "#D4D6D7",
      200: "#A9ADB0",
      300: "#7D8488",
      400: "#525B61",
      500: "#273239",
      600: "#1F282E",
      700: "#171E22",
      800: "#101417",
      900: "#080A0B",
      1000: "#020303",
    },
    accent: {
      50: "#F4F5F5",
      100: "#D4D6D7",
      200: "#A9ADB0",
      300: "#7D8488",
      400: "#525B61",
      500: "#273239",
      600: "#1F282E",
      700: "#171E22",
      800: "#101417",
      900: "#080A0B",
      1000: "#020303",
    },
    neutral: {
      50: "#F4F5F5",
      100: "#D4D6D7",
      200: "#A9ADB0",
      300: "#7D8488",
      400: "#525B61",
      500: "#273239",
      600: "#1F282E",
      700: "#171E22",
      800: "#101417",
      900: "#080A0B",
      1000: "#020303",
    },
    background: {
      50: "#FEFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F8F9F9",
      400: "#F6F7F7",
      500: "#f4f5f5",
      600: "#C3C4C4",
      700: "#929393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.NavyBlue]: {
    primary: {
      50: "#F2F5FB",
      100: "#CCD8ED",
      200: "#99B1DB",
      300: "#668BCA",
      400: "#3364B8",
      500: "#003da6",
      600: "#003185",
      700: "#002564",
      800: "#001842",
      900: "#000C21",
      1000: "#000308",
    },
    secondary: {
      50: "#F2F4F8",
      100: "#CCD5E3",
      200: "#99AAC7",
      300: "#6680AC",
      400: "#335590",
      500: "#002b74",
      600: "#00225D",
      700: "#001A46",
      800: "#00112E",
      900: "#000917",
      1000: "#000206",
    },
    accent: {
      50: "#F2F5FB",
      100: "#CCD8ED",
      200: "#99B1DB",
      300: "#668BCA",
      400: "#3364B8",
      500: "#003da6",
      600: "#003185",
      700: "#002564",
      800: "#001842",
      900: "#000C21",
      1000: "#000308",
    },
    neutral: {
      50: "#F6F6F6",
      100: "#DBDCDD",
      200: "#B7B8BB",
      300: "#949598",
      400: "#707176",
      500: "#4c4e54",
      600: "#3D3E43",
      700: "#2E2F32",
      800: "#1E1F22",
      900: "#0F1011",
      1000: "#040404",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFEFF",
      200: "#FBFDFF",
      300: "#F9FBFF",
      400: "#F7FAFF",
      500: "#f5f9ff",
      600: "#C4C7CC",
      700: "#939599",
      800: "#626466",
      900: "#313233",
      1000: "#0C0C0D",
    },
  },
  [environmentTheme.NavyAzure]: {
    primary: {
      50: "#F3F4F6",
      100: "#CDD3DA",
      200: "#9CA7B5",
      300: "#6A7C91",
      400: "#39506C",
      500: "#072447",
      600: "#061D39",
      700: "#04162B",
      800: "#030E1C",
      900: "#01070E",
      1000: "#000204",
    },
    secondary: {
      50: "#F3F4F6",
      100: "#CDD3DA",
      200: "#9CA7B5",
      300: "#6A7C91",
      400: "#39506C",
      500: "#072447",
      600: "#061D39",
      700: "#04162B",
      800: "#030E1C",
      900: "#01070E",
      1000: "#000204",
    },
    accent: {
      50: "#F3F4F6",
      100: "#CDD3DA",
      200: "#9CA7B5",
      300: "#6A7C91",
      400: "#39506C",
      500: "#072447",
      600: "#061D39",
      700: "#04162B",
      800: "#030E1C",
      900: "#01070E",
      1000: "#000204",
    },
    neutral: {
      50: "#F3F4F6",
      100: "#CDD3DA",
      200: "#9CA7B5",
      300: "#6A7C91",
      400: "#39506C",
      500: "#072447",
      600: "#061D39",
      700: "#04162B",
      800: "#030E1C",
      900: "#01070E",
      1000: "#000204",
    },
    background: {
      50: "#FFFFFF",
      100: "#FEFEFE",
      200: "#FDFDFD",
      300: "#FCFCFC",
      400: "#FBFBFB",
      500: "#FAFAFA",
      600: "#C8C8C8",
      700: "#969696",
      800: "#646464",
      900: "#323232",
      1000: "#0D0D0D",
    },
  },
  [environmentTheme.Gold]: {
    primary: {
      50: "#F7F7F7",
      100: "#DEDEDE",
      200: "#BCBDBD",
      300: "#9B9B9D",
      400: "#797A7C",
      500: "#58595b",
      600: "#464749",
      700: "#353537",
      800: "#232424",
      900: "#121212",
      1000: "#040405",
    },
    secondary: {
      50: "#F7F7F7",
      100: "#DEDEDE",
      200: "#BCBDBD",
      300: "#9B9B9D",
      400: "#797A7C",
      500: "#58595b",
      600: "#464749",
      700: "#353537",
      800: "#232424",
      900: "#121212",
      1000: "#040405",
    },
    accent: {
      50: "#FBF9F5",
      100: "#F0E8D5",
      200: "#E2D1AB",
      300: "#D3BB82",
      400: "#C5A458",
      500: "#b68d2e",
      600: "#927125",
      700: "#6D551C",
      800: "#493812",
      900: "#241C09",
      1000: "#090702",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D4",
      200: "#A7A8A9",
      300: "#7B7C7E",
      400: "#4F5153",
      500: "#232528",
      600: "#1C1E20",
      700: "#151618",
      800: "#0E0F10",
      900: "#070708",
      1000: "#020202",
    },
    background: {
      50: "#FEFEFE",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F8F8F8",
      400: "#F6F6F6",
      500: "#f4f4f4",
      600: "#C3C3C3",
      700: "#929292",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Orange]: {
    primary: {
      50: "#FFF7F2",
      100: "#FFDFCC",
      200: "#FFBF99",
      300: "#FF9E66",
      400: "#FF7E33",
      500: "#ff5e00",
      600: "#CC4B00",
      700: "#993800",
      800: "#662600",
      900: "#331300",
      1000: "#0D0500",
    },
    secondary: {
      50: "#FFF7F2",
      100: "#FFDFCC",
      200: "#FFBF99",
      300: "#FF9E66",
      400: "#FF7E33",
      500: "#ff5e00",
      600: "#CC4B00",
      700: "#993800",
      800: "#662600",
      900: "#331300",
      1000: "#0D0500",
    },
    accent: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A8A8A8",
      300: "#7C7C7C",
      400: "#515151",
      500: "#252525",
      600: "#1E1E1E",
      700: "#161616",
      800: "#0F0F0F",
      900: "#070707",
      1000: "#020202",
    },
    neutral: {
      50: "#F6F6F6",
      100: "#DBDBDB",
      200: "#B7B7B7",
      300: "#949494",
      400: "#707070",
      500: "#4c4c4c",
      600: "#3D3D3D",
      700: "#2E2E2E",
      800: "#1E1E1E",
      900: "#0F0F0F",
      1000: "#040404",
    },
    background: {
      50: "#FEFEFE",
      100: "#FDFCFC",
      200: "#FBFAF9",
      300: "#F8F7F6",
      400: "#F6F5F3",
      500: "#f4f2f0",
      600: "#C3C2C0",
      700: "#929190",
      800: "#626160",
      900: "#313030",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Yellow]: {
    primary: {
      50: "#FEFBF2",
      100: "#FBF1CC",
      200: "#F7E399",
      300: "#F3D466",
      400: "#EFC633",
      500: "#ebb800",
      600: "#BC9300",
      700: "#8D6E00",
      800: "#5E4A00",
      900: "#2F2500",
      1000: "#0C0900",
    },
    secondary: {
      50: "#FEFBF2",
      100: "#FBF1CC",
      200: "#F7E399",
      300: "#F3D466",
      400: "#EFC633",
      500: "#ebb800",
      600: "#BC9300",
      700: "#8D6E00",
      800: "#5E4A00",
      900: "#2F2500",
      1000: "#0C0900",
    },
    accent: {
      50: "#FEFCF2",
      100: "#FCF3CD",
      200: "#F9E79A",
      300: "#F5DA68",
      400: "#F2CE35",
      500: "#efc203",
      600: "#BF9B02",
      700: "#8F7402",
      800: "#604E01",
      900: "#302701",
      1000: "#0C0A00",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A7A7A7",
      300: "#7C7C7C",
      400: "#505050",
      500: "#242424",
      600: "#1D1D1D",
      700: "#161616",
      800: "#0E0E0E",
      900: "#070707",
      1000: "#020202",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F9F9F9",
      400: "#F7F7F7",
      500: "#f5f5f5",
      600: "#C4C4C4",
      700: "#939393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Green]: {
    primary: {
      50: "#F3F6F2",
      100: "#CED9CC",
      200: "#9CB399",
      300: "#6B8E66",
      400: "#396833",
      500: "#084200",
      600: "#063500",
      700: "#052800",
      800: "#031A00",
      900: "#020D00",
      1000: "#000300",
    },
    secondary: {
      50: "#F3F9F2",
      100: "#CFE9CC",
      200: "#A0D299",
      300: "#70BC66",
      400: "#41A533",
      500: "#118f00",
      600: "#0E7200",
      700: "#0A5600",
      800: "#073900",
      900: "#031D00",
      1000: "#010700",
    },
    accent: {
      50: "#F3F6F2",
      100: "#CED9CC",
      200: "#9CB399",
      300: "#6B8E66",
      400: "#396833",
      500: "#084200",
      600: "#063500",
      700: "#052800",
      800: "#031A00",
      900: "#020D00",
      1000: "#000300",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A7A7A7",
      300: "#7C7C7C",
      400: "#505050",
      500: "#242424",
      600: "#1D1D1D",
      700: "#161616",
      800: "#0E0E0E",
      900: "#070707",
      1000: "#020202",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F9F9F9",
      400: "#F7F7F7",
      500: "#f5f5f5",
      600: "#C4C4C4",
      700: "#939393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Blue]: {
    primary: {
      50: "#F3F2FC",
      100: "#D1CCF4",
      200: "#A299E9",
      300: "#7466DD",
      400: "#4533D2",
      500: "#1700c7",
      600: "#12009F",
      700: "#0E0077",
      800: "#090050",
      900: "#050028",
      1000: "#01000A",
    },
    secondary: {
      50: "#F3F2FC",
      100: "#D1CCF4",
      200: "#A299E9",
      300: "#7466DD",
      400: "#4533D2",
      500: "#1700c7",
      600: "#12009F",
      700: "#0E0077",
      800: "#090050",
      900: "#050028",
      1000: "#01000A",
    },
    accent: {
      50: "#F3F2F6",
      100: "#CECCDB",
      200: "#9D99B8",
      300: "#6B6694",
      400: "#3A3371",
      500: "#09004d",
      600: "#07003E",
      700: "#05002E",
      800: "#04001F",
      900: "#02000F",
      1000: "#000004",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A7A7A7",
      300: "#7C7C7C",
      400: "#505050",
      500: "#242424",
      600: "#1D1D1D",
      700: "#161616",
      800: "#0E0E0E",
      900: "#070707",
      1000: "#020202",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F9F9F9",
      400: "#F7F7F7",
      500: "#f5f5f5",
      600: "#C4C4C4",
      700: "#939393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Purple]: {
    primary: {
      50: "#F7F2FB",
      100: "#DECCF0",
      200: "#BD99E1",
      300: "#9B66D1",
      400: "#7A33C2",
      500: "#5900b3",
      600: "#47008F",
      700: "#35006B",
      800: "#240048",
      900: "#120024",
      1000: "#040009",
    },
    secondary: {
      50: "#F7F2FB",
      100: "#DECCF0",
      200: "#BD99E1",
      300: "#9B66D1",
      400: "#7A33C2",
      500: "#5900b3",
      600: "#47008F",
      700: "#35006B",
      800: "#240048",
      900: "#120024",
      1000: "#040009",
    },
    accent: {
      50: "#F5F2F7",
      100: "#D6CCE0",
      200: "#AD99C2",
      300: "#8566A3",
      400: "#5C3385",
      500: "#330066",
      600: "#290052",
      700: "#1F003D",
      800: "#140029",
      900: "#0A0014",
      1000: "#030005",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A7A7A7",
      300: "#7C7C7C",
      400: "#505050",
      500: "#242424",
      600: "#1D1D1D",
      700: "#161616",
      800: "#0E0E0E",
      900: "#070707",
      1000: "#020202",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F9F9F9",
      400: "#F7F7F7",
      500: "#f5f5f5",
      600: "#C4C4C4",
      700: "#939393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
  [environmentTheme.Pink]: {
    primary: {
      50: "#FDF2F9",
      100: "#F7CCE5",
      200: "#EF99CB",
      300: "#E666B1",
      400: "#DE3397",
      500: "#d6007d",
      600: "#AB0064",
      700: "#80004B",
      800: "#560032",
      900: "#2B0019",
      1000: "#0B0006",
    },
    secondary: {
      50: "#FBF2F7",
      100: "#F0CCE1",
      200: "#E199C3",
      300: "#D166A4",
      400: "#C23386",
      500: "#b30068",
      600: "#8F0053",
      700: "#6B003E",
      800: "#48002A",
      900: "#240015",
      1000: "#090005",
    },
    accent: {
      50: "#FDF2F9",
      100: "#F7CCE5",
      200: "#EF99CB",
      300: "#E666B1",
      400: "#DE3397",
      500: "#d6007d",
      600: "#AB0064",
      700: "#80004B",
      800: "#560032",
      900: "#2B0019",
      1000: "#0B0006",
    },
    neutral: {
      50: "#F4F4F4",
      100: "#D3D3D3",
      200: "#A7A7A7",
      300: "#7C7C7C",
      400: "#505050",
      500: "#242424",
      600: "#1D1D1D",
      700: "#161616",
      800: "#0E0E0E",
      900: "#070707",
      1000: "#020202",
    },
    background: {
      50: "#FFFFFF",
      100: "#FDFDFD",
      200: "#FBFBFB",
      300: "#F9F9F9",
      400: "#F7F7F7",
      500: "#f5f5f5",
      600: "#C4C4C4",
      700: "#939393",
      800: "#626262",
      900: "#313131",
      1000: "#0C0C0C",
    },
  },
};

/**
 * Use themes instead
 */
export const deprecatedAuremColours = {
  black: "#000000",
  white: "#FFFFFF",
  primary10: "#FEFFE9",
  primary20: "#F5F7C8",
  primary40: "#E7ED93",
  primary60: "#F0FE53",
  primary80: "#C3C644",
  primary90: "#3E4016",
  neutral10: "#EFEFEF",
  neutral20: "#DEDEDE",
  neutral30: "#CFCFCF",
  neutral40: "#B5B5B5",
  neutral50: "#8F8F8F",
  neutral60: "#696969",
  neutral70: "#525252",
  neutral80: "#454545",
  neutral90: "#333333",
  success0: "#E4F0E2",
  success20: "#9ADE91",
  success40: "#60C254",
  success60: "#3FA331",
  success80: "#2A6121",
  success100: "#1C3918",
  warning0: "#FCEACC",
  warning20: "#D3BF8E",
  warning40: "#CE9229",
  warning60: "#BD6900",
  warning80: "#76410A",
  warning100: "#522600",
  danger0: "#FAE6E3",
  danger20: "#F3ABA2",
  danger40: "#EF7666",
  danger60: "#E84933",
  danger80: "#8F2F23",
  danger100: "#4E1D18",
  green10: "#F2F7ED",
  green20: "#B6F59D",
  green40: "#99EA78",
  green50: "#81BF63",
  green60: "#5F8C48",
  green80: "#3C592E",
  green90: "#2D4222",
  blue10: "#EFF6FA",
  blue20: "#BFE1F5",
  blue40: "#A7D2EB",
  blue50: "#75B0D1",
  blue60: "#547D94",
  blue80: "#364F5E",
  blue90: "#293C47",
  purple10: "#F3F3FF",
  purple20: "#D5D5F5",
  purple40: "#C1C1EB",
  purple50: "#9A98ED",
  purple60: "#74748A",
  purple80: "#494957",
  purple90: "#383842",
  pink10: "#FCF0F7",
  pink20: "#F5C9E3",
  pink40: "#EBB0D3",
  pink50: "#D487B5",
  pink60: "#807079",
  pink80: "#52474E",
  pink90: "#3D363A",
};
